"use client";

import { ArrowUpRight, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ChannelManagerHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.18
  });
  return <section data-section-id="1708" id="channelmanagerhero" className="relative overflow-hidden bg-background py-24 md:py-44" style={{
    borderBottom: "1px solid var(--border)"
  }}>
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")"
    }} />

      <div ref={ref} className="container relative mx-auto max-w-[1320px] px-5 md:px-8 lg:px-14">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10 items-center">
          {}
          <div className="relative lg:col-span-7 transition-all duration-700" style={{
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)"
        }}>
            {}
            <div aria-hidden="true" className="absolute -bottom-6 -right-2 h-[88%] w-[78%] opacity-[0.12]" style={{
            background: "var(--secondary)",
            borderRadius: "62% 38% 54% 46% / 48% 56% 44% 52%",
            transform: "translate(24px, 24px) rotate(-6deg)"
          }} />

            <div className="relative overflow-hidden shadow-lg" style={{
            borderRadius: "18px 28px 64px 18px",
            clipPath: "path('M 40 0 C 180 6 360 0 520 12 C 600 18 612 80 608 180 C 604 300 612 420 600 520 C 588 600 480 612 320 608 C 180 604 60 612 24 588 C 0 540 6 360 0 200 C 0 80 8 12 40 0 Z')"
          }}>
              <div className="aspect-[5/6] w-full bg-cover bg-center" style={{
              backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png)"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png-wvc-sizes" alt="Professional hotel revenue management dashboard showing growth analytics" loading="lazy" decoding="async" className="h-full w-full object-cover opacity-0" />
              </div>
            </div>

            {}
            <div className="absolute -bottom-6 left-4 md:-bottom-8 md:left-8 z-10 flex h-22 w-22 md:h-28 md:w-28 items-center justify-center rounded-lg" style={{
            width: "7rem",
            height: "7rem",
            borderRadius: "9999px",
            background: "var(--primary)",
            color: "var(--primary-foreground)",
            transform: "rotate(-8deg)",
            boxShadow: "var(--shadow-lg)"
          }} aria-hidden="true">
              <svg viewBox="0 0 120 120" className="h-full w-full">
                <defs>
                  <path id="seal-curve" d="M 60,60 m -42,0 a 42,42 0 1,1 84,0 a 42,42 0 1,1 -84,0" />
                </defs>
                <text fill="currentColor" style={{
                fontFamily: "var(--font-mono)",
                fontSize: "10px",
                letterSpacing: "0.22em"
              }}>
                  <textPath href="#seal-curve" startOffset="0">
                    EST · REVENUE · 2024 · HOTELREV · BABA ·
                  </textPath>
                </text>
                <circle cx="60" cy="60" r="22" fill="none" stroke="currentColor" strokeWidth="1" />
                <text x="60" y="65" textAnchor="middle" fill="currentColor" style={{
                fontFamily: "var(--font-serif)",
                fontSize: "14px",
                fontStyle: "italic"
              }}>
                  HRB
                </text>
              </svg>
            </div>
          </div>

          {}
          <div className="relative lg:col-span-5">
            {}
            <div className="flex items-center gap-3 transition-all duration-700" style={{
            transitionDelay: "80ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <span className="text-foreground/70" style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.75rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase"
            }}>
                № 01 / Revenue Expertise
              </span>
              <span className="inline-block" style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontSize: "1.25rem",
              color: "var(--primary)",
              transform: "rotate(-2deg)",
              lineHeight: 1
            }}>
                — data-driven
              </span>
            </div>

            {}
            <h1 className="mt-6 font-default text-foreground transition-all duration-700" style={{
            fontSize: "clamp(2.5rem, 5.6vw, 4.75rem)",
            fontWeight: 700,
            letterSpacing: "-0.025em",
            lineHeight: 1.04,
            transitionDelay: "160ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              Maximize Your Hotel’s Revenue with Data-Driven{" "}
              <span className="relative inline-block">
                <span style={{
                fontFamily: "var(--font-serif)",
                fontStyle: "italic",
                fontSize: "1.12em",
                color: "var(--secondary)",
                fontWeight: 500
              }}>
                  OTA Strategies
                </span>
                {}
                <svg aria-hidden="true" viewBox="0 0 320 120" className="pointer-events-none absolute -left-4 -top-3 h-[120%] w-[115%]" preserveAspectRatio="none">
                  <path d="M 30 70 C 10 30, 80 8, 170 12 C 270 16, 312 38, 304 72 C 296 104, 220 112, 140 108 C 60 104, 8 92, 20 60" fill="none" stroke="var(--secondary)" strokeWidth="2" strokeLinecap="round" style={{
                  strokeDasharray: 900,
                  strokeDashoffset: isVisible ? 0 : 900,
                  transition: "stroke-dashoffset 900ms ease-out 1100ms"
                }} />
                </svg>
              </span>
              .
            </h1>

            {}
            <p className="mt-7 max-w-xl transition-all duration-700" style={{
            fontSize: "clamp(1.0625rem, 1.1vw, 1.2rem)",
            lineHeight: 1.62,
            color: "var(--foreground)",
            transitionDelay: "260ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              Sunil Singh helps hotel owners and investors unlock hidden potential through expert revenue management, tech stack optimization, and high-conversion OTA strategies.
            </p>

            {}
            <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center transition-all duration-700" style={{
            transitionDelay: "340ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <Button asChild size="lg" className="rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/85 px-7 py-6 text-base font-medium shadow-sm" style={{
              borderRadius: "9999px"
            }}>
                <Link to="/ContactPage">
                  Book a Consultation
                  <ArrowUpRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>

              <Link to="/OurServicesPage" className="group relative inline-flex items-center text-foreground" style={{
              fontFamily: "var(--font-sans)",
              fontSize: "1rem",
              fontWeight: 500,
              color: "var(--secondary)"
            }}>
                <span className="relative">
                  Explore Our Services
                  <svg aria-hidden="true" viewBox="0 0 200 12" className="absolute -bottom-2 left-0 h-2 w-full" preserveAspectRatio="none">
                    <path d="M 2 7 C 30 2, 70 10, 110 5 C 150 1, 180 9, 198 6" fill="none" stroke="var(--secondary)" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </span>
                <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>

            {}
            <div className="mt-10 flex items-start gap-3 transition-all duration-700" style={{
            transitionDelay: "420ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <svg aria-hidden="true" viewBox="0 0 60 40" className="mt-1 h-6 w-9 shrink-0">
                <path d="M 4 6 C 18 12, 28 22, 40 28 M 40 28 L 32 22 M 40 28 L 34 32" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" style={{
                color: "var(--secondary)"
              }} aria-hidden="true" />
                <span className="text-foreground/75" style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.6875rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase"
              }}>
                  Expert Revenue Audit
                </span>
                <span style={{
                fontFamily: "var(--font-serif)",
                fontStyle: "italic",
                fontSize: "1.1rem",
                color: "var(--primary)",
                transform: "rotate(-1.5deg)",
                display: "inline-block"
              }}>
                  — available now.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}